import {
  formatMoney,
  formatRange,
  getPlan,
  Popover,
  type Money,
  type Plan,
} from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The trip's budget on the bar, and pressed what it stands at: what the trip is expected to cost,
 * its lowest to highest, what is paid and still to pay, and what is left of the budget or over it.
 */
export function BarBudget({ trip, budget }: { trip: string; budget: Money }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState<Plan | null>(null);

  // As it is now: read each time it is opened.
  useEffect(() => {
    if (!open) return;
    getPlan(trip).then(setPlan, () => undefined);
  }, [open, trip]);

  const money = (amount: Money) => formatMoney(amount, lang);
  // Another trip's, read before this one was opened, is not this one's.
  const totals = plan?.trip.id === trip ? plan.totals : undefined;
  const left = totals && { ...budget, minor: budget.minor - totals.expected.minor };
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      label={t('tabs.budgetTitle')}
      trigger={(props) => (
        <button
          type="button"
          className="bar-budget"
          {...props}
          aria-label={`${t('tabs.budget')} ${money(budget)}`}
        >
          <span>{t('tabs.budget')}</span>
          <span className="mono">{money(budget)}</span>
        </button>
      )}
    >
      <div className="bar-budget-panel">
        <p className="bar-budget-head">{t('tabs.budgetTitle')}</p>
        <dl>
          <dt>{t('tabs.budget')}</dt>
          <dd className="mono">{money(budget)}</dd>
          {totals && left && (
            <>
              <dt>{t('tabs.expected')}</dt>
              <dd className="mono strong">{money(totals.expected)}</dd>
              <dt>{t('tabs.range')}</dt>
              <dd className="mono">{formatRange(totals.total, lang)}</dd>
              <dt>{t('tabs.paid')}</dt>
              <dd className="mono">{money(totals.paid)}</dd>
              {/* Of it, the documents': the insurances, the visas. */}
              {totals.documents.minor > 0 && (
                <>
                  <dt>{t('tabs.documents')}</dt>
                  <dd className="mono">{money(totals.documents)}</dd>
                </>
              )}
              <dt>{t('tabs.toPay')}</dt>
              <dd className="mono">{money(totals.toPay)}</dd>
              <dt>{t(left.minor < 0 ? 'tabs.over' : 'tabs.left')}</dt>
              <dd
                className={`mono strong ${left.minor < 0 ? 'bar-budget-over' : 'bar-budget-left'}`}
              >
                {money({ ...left, minor: Math.abs(left.minor) })}
              </dd>
            </>
          )}
        </dl>
      </div>
    </Popover>
  );
}
